import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { Link, useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Paper,
  Stack,
  Typography,
  Button,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import Loader from "../../components/Loader";
import UploadFilesModal from "../../components/Modals/FileUploadModal";
import useGetExternalMediaSummary from "../../hooks/useGetExternalMediaSummary";
import useGetExternalMediaTypes from "../../hooks/useGetExternalMediaTypes";
import { UPLOAD_TYPES } from "../../utils/constants";

dayjs.extend(utc);

const PatientExternalMedia = ({ mediaType = "photo" }) => {
  const { _id_ } = useParams();
  const navigate = useNavigate();

  const isPhoto = mediaType === "photo";
  const routeSegment = isPhoto ? "sessions_external_photos" : "sessions_external_videos";
  const activeKey = isPhoto ? "externalPhotos" : "externalVideos";
  const label = isPhoto ? "External Photos" : "External Videos";
  const uploadLabel = isPhoto ? "Upload Images" : "Upload Videos";

  const userData = JSON.parse(localStorage.getItem("user_data"));
  const role = userData?.role;

  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();

  setActive(activeKey);
  setBreadcrumbTitle(`${label} - Session View`);

  const {
    data: summaryData,
    isLoading,
    refetch: refetchSummary,
  } = useGetExternalMediaSummary({
    patientId: _id_,
    trialId: trialFilter,
    mediaType,
    enabled: !!trialFilter,
  });

  const [modal, setModal] = useState({ open: false, caseId: null });
  const [files, setFiles] = useState([]);
  const [captureDateTime, setCaptureDateTime] = useState(dayjs().utc());
  const [typeList, setTypeList] = useState([]);

  const { data: typeData = [] } = useGetExternalMediaTypes({
    type: isPhoto ? "photos" : "videos",
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  useEffect(() => {
    setTypeList(typeData);
  }, [typeData]);

  const isUploaderAllowed =
    role !== "CRO Admin" &&
    role !== "Cro_coordinator" &&
    role !== "Data Manager" &&
    role !== "Reviewer";

  const routeTo = (id, visitName) => {
    navigate(`/patient/${_id_}/${routeSegment}/${id}?trial=${trialFilter}`, {
      state: { visitName },
    });
  };

  const openModal = (caseId) => {
    setFiles([]);
    setModal({ open: true, caseId });
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = { whiteSpace: "nowrap" };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Upload modal */}
      <UploadFilesModal
        uploadType={isPhoto ? UPLOAD_TYPES.EXTERNAL_PHOTOS : UPLOAD_TYPES.EXTERNAL_VIDEOS}
        showModal={modal.open}
        setShowModal={(open) => setModal((prev) => ({ ...prev, open }))}
        files={files}
        setFiles={setFiles}
        captureDateTime={captureDateTime}
        setCaptureDateTime={setCaptureDateTime}
        acceptedMimeTypes={
          isPhoto
            ? ["image/gif", "image/jpeg", "image/png", "image/svg+xml"]
            : ["video/mp4", "video/mpeg", "video/webm", "video/quicktime"]
        }
        typeList={typeList}
        caseId={modal.caseId}
        getExternalMedia={refetchSummary}
        trialId={trialFilter}
      />

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{label} - Session View</Typography>

        <Button
          component={Link}
          to={`/patient/${_id_}/${routeSegment}_gallery?trial=${trialFilter}`}
          variant="outlined"
          size="small"
        >
          Gallery View
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Visit Name</TableCell>
              <TableCell sx={headerCellSx}>
                {isPhoto ? "Expected Images" : "Expected Videos"}
              </TableCell>
              <TableCell sx={headerCellSx}>
                {isPhoto ? "Uploaded Images" : "Uploaded Videos"}
              </TableCell>
              <TableCell sx={headerCellSx}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              summaryData?.sessions?.map((session) => (
                <TableRow key={session.session_id} hover>
                  <TableCell sx={bodyCellSx}>{session.visit_name}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    {isPhoto ? session.expected_images : session.expected_videos}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {isPhoto ? session.uploaded_images : session.uploaded_videos}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MuiLink
                        component="button"
                        underline="hover"
                        onClick={() => routeTo(session.session_id, session.visit_name)}
                      >
                        View
                      </MuiLink>

                      {isUploaderAllowed && (
                        <>
                          <Typography component="span" color="text.secondary">
                            /
                          </Typography>
                          <MuiLink
                            component="button"
                            underline="hover"
                            onClick={() => openModal(session.session_id)}
                          >
                            {uploadLabel}
                          </MuiLink>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && !summaryData?.sessions?.length && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No {label.toLowerCase()} found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PatientExternalMedia;
